import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, MailCheck } from "lucide-react";
import { Label } from "./ui-bits";
import { api, useAuth } from "@/lib/auth";

const SEMBLE_ORIGIN = "https://online-booking.semble.io";
const TOKEN = process.env.REACT_APP_SEMBLE_TOKEN;
const COMPLETED = ["confirmed", "booked", "completed", "complete", "success", "paid"];

function ConfirmationPanel({ event, onDone }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [status, setStatus] = useState(event.emailed ? "sent" : "idle");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("busy"); setError("");
    try {
      await api(`/bookings/events/${event.id}/email`, { method: "POST", body: { email, name: name || null } });
      setStatus("sent");
      onDone?.(email);
    } catch (err) { setStatus("error"); setError(err.message); }
  };

  return (
    <div className="card-line rounded-2xl p-6 sm:p-8 border-l-2 !border-l-teal" data-testid="booking-confirmation-panel">
      <Label><MailCheck size={14} /> Booking received</Label>
      {status === "sent" ? (
        <p className="text-bone/90 mt-4" data-testid="booking-confirmation-sent">
          Your confirmation and preparation instructions have been emailed{event.emailed_to || email ? <> to <span className="text-teal">{event.emailed_to || email}</span></> : null}. Semble sends your calendar confirmation separately.
        </p>
      ) : (
        <form onSubmit={submit} className="mt-4 space-y-3">
          <p className="text-mute text-sm">Where should we send your preparation instructions (morning timing, fasting, what to bring)?</p>
          <div className="grid sm:grid-cols-[1fr,1.3fr,auto] gap-3">
            <input className="field" placeholder="First name (optional)" value={name} onChange={(e) => setName(e.target.value)} data-testid="booking-confirm-name" />
            <input type="email" required className="field" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="booking-confirm-email" />
            <button type="submit" disabled={status === "busy"} className="btn-primary !py-2.5 !px-5 text-sm justify-center" data-testid="booking-confirm-submit">{status === "busy" ? "Sending…" : "Email me"}</button>
          </div>
          {error && <p className="text-rose text-sm" data-testid="booking-confirm-error">{error}</p>}
          <p className="text-xs text-dusk">One email per booking. <Link to="/portal/login" className="text-teal hover:underline">Sign in</Link> before booking next time and this happens automatically.</p>
        </form>
      )}
    </div>
  );
}

export default function SembleBooking() {
  const { user } = useAuth();
  const [event, setEvent] = useState(null);

  useEffect(() => {
    if (!TOKEN) return undefined;
    const onMessage = async (ev) => {
      if (ev.origin !== SEMBLE_ORIGIN || !ev.data || !ev.data.bookingStatus) return;
      const d = ev.data;
      try {
        const res = await api("/bookings/events", {
          method: "POST",
          body: { booking_status: String(d.bookingStatus), booking_start: d.bookingStart || null, product_name: d.productName || null, practitioner: d.practitioner || null, page: window.location.pathname },
        });
        if (res.completed || COMPLETED.includes(String(d.bookingStatus).toLowerCase())) setEvent(res);
      } catch {}
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  if (!TOKEN) {
    return (
      <div className="card-line rounded-2xl p-8 sm:p-12 text-center" data-testid="booking-placeholder">
        <div className="w-14 h-14 rounded-full bg-teal/10 border border-teal/40 grid place-items-center mx-auto"><CalendarClock size={22} className="text-teal" /></div>
        <Label className="justify-center mt-6">Online booking opens soon</Label>
        <h3 className="font-display text-3xl sm:text-4xl tracking-wide mt-4">Secure booking is being connected.</h3>
        <Link to="/contact" className="btn-primary mt-8" data-testid="booking-placeholder-enquire">Send an enquiry instead</Link>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {!user && (
        <p className="text-xs text-dusk" data-testid="booking-signin-hint">
          <Link to="/portal/login" className="text-teal hover:underline">Sign in to the patient portal</Link> before booking and we'll email your preparation instructions automatically.
        </p>
      )}
      {event && <ConfirmationPanel event={event} />}
      <div className="card-line rounded-2xl overflow-hidden" data-testid="booking-iframe-wrap">
        <iframe
          title="Book an appointment with Androgen Health"
          src={`${SEMBLE_ORIGIN}/?token=${encodeURIComponent(TOKEN)}`}
          width="100%"
          height="820"
          frameBorder="0"
          scrolling="auto"
          allow="payment *"
          className="block bg-white"
          data-testid="semble-booking-iframe"
        />
      </div>
    </div>
  );
}
